import { useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import * as Popover from "@radix-ui/react-popover";
import { keyframes, styled } from "@pigment-css/react";
import { CheckMark2Small } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { SHEET_Z_INDEX } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { useHasFinePointer } from "@acto/taskpane/field-sales-2/utils/hooks/useHasFinePointer";
import { menuItemCheckStyle, menuItemStyle, menuSurfaceStyle } from "@acto/taskpane/field-sales-2/styles/glassMenu";
import { fieldLabelStyle, fieldPillStyle, fieldValueStyle } from "./fieldStyles";
import { parseCustomTime } from "./timeSlots";

const Trigger = styled(Popover.Trigger)(({ theme }) => ({
  ...fieldPillStyle(theme),
  background: "transparent",
  WebkitTapHighlightColor: "transparent",
  '&[data-state="open"]': { borderColor: theme.fieldSalesTwo.colors.neutral[900] },
}));

const Label = styled("span")(({ theme }) => fieldLabelStyle(theme));
const Value = styled("span")(({ theme }) => fieldValueStyle(theme));

const popoverIn = keyframes({
  from: { opacity: 0, transform: "translateY(-8px) scale(0.96)" },
  to: { opacity: 1, transform: "translateY(0) scale(1)" },
});

const popoverInFromBelow = keyframes({
  from: { opacity: 0, transform: "translateY(8px) scale(0.96)" },
  to: { opacity: 1, transform: "translateY(0) scale(1)" },
});

const Content = styled(Popover.Content)(({ theme }) => ({
  ...menuSurfaceStyle(theme),
  zIndex: SHEET_Z_INDEX + 1,
  // At least as wide as the custom entry row (two 3rem segments + apply button), which cannot
  // shrink; capped by Radix's measured free space so a narrow pane shifts the popover instead of
  // clipping it.
  width: "max(var(--radix-popover-trigger-width), 13rem)",
  maxWidth: "var(--radix-popover-content-available-width)",
  maxHeight: "var(--radix-popover-content-available-height)",
  transformOrigin: "var(--radix-popover-content-transform-origin)",
  outline: "none",
  animation: `${popoverIn} ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  '&[data-side="top"]': {
    animationName: popoverInFromBelow,
  },
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
}));

// role="listbox" must not contain text inputs, so CustomTimeEntry sits outside it as a
// non-scrolling footer.
const OptionList = styled("div")({
  position: "relative",
  flex: "0 1 auto",
  minHeight: 0,
  maxHeight: "13rem",
  overflowY: "auto",
  overscrollBehavior: "contain",
  scrollbarWidth: "thin",
  display: "flex",
  flexDirection: "column",
});

const Item = styled("button")(({ theme }) => ({
  ...menuItemStyle(theme),
  justifyContent: "space-between",
}));

const ItemCheck = styled("span")(({ theme }) => menuItemCheckStyle(theme));

const CustomEntry = styled("div")(({ theme }) => ({
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[8],
  marginTop: theme.fieldSalesTwo.spacing[8],
  paddingTop: theme.fieldSalesTwo.spacing[8],
  borderTop: `1px solid ${theme.fieldSalesTwo.colors.neutral[200]}`,
}));

const CustomLabel = styled("span")(({ theme }) => ({
  paddingInline: theme.fieldSalesTwo.spacing[8],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const CustomRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  paddingInline: theme.fieldSalesTwo.spacing[8],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Segment = styled("input")(({ theme }) => ({
  width: "3rem",
  minWidth: 0,
  height: theme.fieldSalesTwo.spacing[44],
  boxSizing: "border-box",
  padding: 0,
  margin: 0,
  border: `1px solid ${theme.fieldSalesTwo.colors.neutral[300]}`,
  borderRadius: theme.fieldSalesTwo.radius[10],
  background: "transparent",
  textAlign: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.input,
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  outline: "none",
  "&:focus-visible": { borderColor: theme.fieldSalesTwo.colors.neutral[900] },
}));

const Apply = styled("button")(({ theme }) => ({
  marginInlineStart: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: theme.fieldSalesTwo.spacing[44],
  height: theme.fieldSalesTwo.spacing[44],
  border: "none",
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.neutral[900],
  color: "#fff",
  cursor: "pointer",
  "&:disabled": { opacity: 0.35, cursor: "default" },
  "& svg": { width: 16, height: 16 },
}));

const clampDigits = (raw: string): string => raw.replace(/\D/g, "").slice(0, 2);

/** Lets the user type an arbitrary time (hours, then minutes) not on the 15-minute grid. */
function CustomTimeEntry({
  value,
  onApply,
  min,
}: {
  value: string;
  onApply: (time: string) => void;
  /** Exclusive lower bound "HH:mm": a valid time must be strictly after it. */
  min?: string;
}) {
  const t = useTranslations("fieldSalesTwo.planMeeting");
  const tCommon = useTranslations("common");
  const [seedHours, seedMinutes] = value.split(":");
  const [hours, setHours] = useState(seedHours ?? "");
  const [minutes, setMinutes] = useState(seedMinutes ?? "");
  const minutesRef = useRef<HTMLInputElement>(null);

  const parsed = parseCustomTime(hours, minutes);
  const isValid = parsed !== null && (min === undefined || parsed > min);
  const apply = () => {
    if (parsed && isValid) onApply(parsed);
  };

  const handleHours = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "");
    if (digits.length > 2) {
      // A paste like "0930": first two digits are the hour, the rest the minutes.
      setHours(digits.slice(0, 2));
      setMinutes(digits.slice(2, 4));
      minutesRef.current?.focus();
      return;
    }
    setHours(digits);
    // A leading digit >2 can only be a one-digit hour (max 23), so the hour is complete.
    if (digits.length === 2 || Number(digits) > 2) minutesRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      apply();
    }
  };

  return (
    <CustomEntry>
      <CustomLabel>{tCommon("custom")}</CustomLabel>
      <CustomRow>
        <Segment
          inputMode="numeric"
          value={hours}
          onChange={handleHours}
          onKeyDown={handleKeyDown}
          placeholder="--"
          aria-label={t("hours")}
        />
        <span aria-hidden="true">:</span>
        <Segment
          ref={minutesRef}
          inputMode="numeric"
          value={minutes}
          onChange={(event) => setMinutes(clampDigits(event.target.value))}
          onKeyDown={handleKeyDown}
          placeholder="--"
          aria-label={t("minutes")}
        />
        <Apply type="button" onClick={apply} disabled={!isValid} aria-label={t("applyCustomTime")}>
          <CheckMark2Small aria-hidden="true" />
        </Apply>
      </CustomRow>
    </CustomEntry>
  );
}

interface TimeSelectProps {
  label: string;
  /** Current "HH:mm". */
  value: string;
  /** Selectable "HH:mm" slots, ascending. */
  options: string[];
  onChange: (value: string) => void;
  /** Exclusive lower bound "HH:mm": the custom entry rejects times not strictly after it. */
  min?: string;
  /** Trigger edge the popover lines up with. On touch the popover is never repositioned to stay in
   * view, so it extends past the trigger's opposite edge as far as its width takes it. */
  align?: "start" | "end";
}

export function TimeSelect({ label, value, options, onChange, min, align = "start" }: TimeSelectProps) {
  const [open, setOpen] = useState(false);
  const hasFinePointer = useHasFinePointer();

  // item.offsetTop is relative to OptionList (position:relative makes it the offsetParent), so this
  // scrolls only the list, not the sheet behind it.
  const centerSelectedInList = (item: HTMLButtonElement | null) => {
    const list = item?.parentElement;
    if (item && list) list.scrollTop = item.offsetTop - list.clientHeight / 2 + item.offsetHeight / 2;
  };

  const commit = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      {/* No aria-label: the "<label> <value>" text content is the button's accessible name, so the
          current time is announced and stays correct as it changes. */}
      <Trigger>
        <Label>{label}</Label>
        <Value>{value}</Value>
      </Trigger>
      <Popover.Portal>
        <Content
          side={hasFinePointer ? "bottom" : "top"}
          sideOffset={8}
          align={align}
          collisionPadding={8}
          // iOS does not shrink the layout viewport (Radix's collision boundary) when the keyboard
          // opens, so Radix would flip the popover down under the keyboard the custom entry raises.
          avoidCollisions={hasFinePointer}
          // The trigger moves while the keyboard animates, without a scroll or resize event to
          // re-run positioning.
          updatePositionStrategy={hasFinePointer ? "optimized" : "always"}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <OptionList role="listbox" aria-label={label}>
            {options.map((option) => (
              <Item
                key={option}
                ref={option === value ? centerSelectedInList : undefined}
                type="button"
                role="option"
                aria-selected={option === value}
                onClick={() => commit(option)}
              >
                {option}
                {option === value && (
                  <ItemCheck>
                    <CheckMark2Small aria-hidden="true" />
                  </ItemCheck>
                )}
              </Item>
            ))}
          </OptionList>
          {/* key: reseed the draft segments whenever the committed value changes. */}
          <CustomTimeEntry key={value} value={value} onApply={commit} min={min} />
        </Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
